import fetch from '../utils/fetch';

const BaseUrl = process.env.MONO_BASE;

const getUrl = (path) => `${BaseUrl}${path}`;

/**
 * Upper bound for the ensure request. The editor awaits this call while adding
 * a QR code, so a stalled request must fail rather than block creation.
 */
export const TRACKING_LINK_ENSURE_TIMEOUT_MS = 10000;

export default {
  /**
   * Get a paginated list of TrackingLinks for the current account.
   * @param {Object} [query] - Query params to send to the API.
   * @returns {Promise} Promise resolving to the API response.
   */
  getTrackingLinks(query = {}) {
    return fetch(getUrl('/tracking-links'), { query });
  },

  /**
   * Get a TrackingLink by its rid.
   * @param {String} rid - TrackingLink rid.
   * @returns {Promise} Promise resolving to the API response.
   */
  getTrackingLinkByRid(rid) {
    return fetch(getUrl(`/tracking-links/by-rid/${rid}`));
  },

  /**
   * Find or create a TrackingLink for a destination URL. The API reuses the
   * oldest existing link for the same destination instead of creating a
   * duplicate.
   *
   * The request is bounded by an AbortController (see
   * `TRACKING_LINK_ENSURE_TIMEOUT_MS`) and rejects with an `AbortError` when it
   * is stalled, so callers fall back instead of waiting indefinitely.
   *
   * @param {Object} opts - Ensure options.
   * @param {String} opts.destinationUrl - Destination the link should resolve to.
   * @param {String} [opts.description] - Human-readable link description.
   * @param {String} [opts.type] - TrackingLink type (`custom`, `partner`).
   * @returns {Promise} Promise resolving to `{ link, created }`.
   */
  async createOrReuseTrackingLink({ destinationUrl, description, type } = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TRACKING_LINK_ENSURE_TIMEOUT_MS,
    );

    try {
      return await fetch(getUrl('/tracking-links/ensure'), {
        method: 'POST',
        body: { destinationUrl, description, type },
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }
  },
};
